import { Injectable, computed, inject, signal, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject } from "rxjs";
import { setDefaultOptions } from "date-fns";
import { enCA, frCA } from "date-fns/locale";
import { LocalStorageService } from "@shared/local-storage";
import { CldrIntlService, IntlService } from "@progress/kendo-angular-intl";
import { TranslateService } from "@ngx-translate/core";
import type { SupportedLanguage } from "@app/views-interfaces";

export const DEFAULT_LANGUAGE: SupportedLanguage = "en-CA";
const LANGUAGE_STORAGE_KEY = "lang";

export function isSupportedLanguage(lang: string | null | undefined): lang is SupportedLanguage {
    return lang === "en-CA" || lang === "fr-CA";
}

/**
 * Single source of truth for the active language.
 *
 * Owns every side effect a language switch implies: localStorage, date-fns defaults, the Kendo
 * locale and ngx-translate's active language. So callers only ever have to call `setLang()`.
 */
@Injectable({ providedIn: "root" })
export class LanguageService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly intlService = inject(IntlService);
    private readonly translateService = inject(TranslateService);

    private readonly langChangeEmitter = new Subject<SupportedLanguage>();
    /** Prefer the `language` signal; this exists for the RxJS call sites that have not migrated. */
    public readonly langChangeEvent$ = this.langChangeEmitter.asObservable();

    private readonly currentLanguage = signal<SupportedLanguage>(LanguageService.readStoredLanguage());

    public readonly language = computed(() => this.currentLanguage());

    /** The other supported language, used to fill gaps in partially translated server data. */
    public readonly fallbackLanguage = computed<SupportedLanguage>(() => (this.currentLanguage() === "en-CA" ? "fr-CA" : "en-CA"));

    constructor() {
        // Seed the store so a language is always persisted, then apply it to the formatting libraries.
        LocalStorageService.setItem(LANGUAGE_STORAGE_KEY, this.currentLanguage());
        this.applyLocale(this.currentLanguage());

        // Stay correct even if something switches ngx-translate directly instead of calling setLang().
        this.translateService.onLangChange.pipe(takeUntilDestroyed()).subscribe((event) => {
            if (isSupportedLanguage(event.lang) && event.lang !== this.currentLanguage()) {
                this.commit(event.lang);
            }
        });

        this.destroyRef.onDestroy(() => {
            this.langChangeEmitter.complete();
        });
    }

    /** Imperative read for call sites that are not yet reactive. Prefer the `language` signal. */
    public getLang(): SupportedLanguage {
        return this.currentLanguage();
    }

    /** Ignores unsupported values, so an unexpected host language leaves the app on its current one. */
    public setLang(lang: string): void {
        if (!isSupportedLanguage(lang)) return;

        this.commit(lang);
        // Last, so the signal is already current when ngx-translate emits onLangChange.
        this.translateService.use(lang);
    }

    private commit(lang: SupportedLanguage): void {
        this.currentLanguage.set(lang);
        LocalStorageService.setItem(LANGUAGE_STORAGE_KEY, lang);
        this.applyLocale(lang);
        this.langChangeEmitter.next(lang);
    }

    private applyLocale(lang: SupportedLanguage): void {
        setDefaultOptions({ locale: lang === "en-CA" ? enCA : frCA });
        (this.intlService as CldrIntlService).localeId = lang;
    }

    private static readStoredLanguage(): SupportedLanguage {
        const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        return isSupportedLanguage(stored) ? stored : DEFAULT_LANGUAGE;
    }
}
